(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-ls-why-choose]';

  class LoveSmithsWhyChoose {
    constructor(root) {
      this.root = root;
      this.grid = root.querySelector('[data-ls-card-grid]');
      this.cards = Array.from(root.querySelectorAll('[data-ls-card]'));
      this.filters = Array.from(root.querySelectorAll('[data-ls-filter]'));
      this.filterJumps = Array.from(root.querySelectorAll('[data-ls-filter-jump]'));
      this.emptyState = root.querySelector('[data-ls-empty-state]');
      this.revealItems = Array.from(root.querySelectorAll('[data-ls-reveal]'));
      this.activeFilter = 'all';
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    init() {
      this.configureFilters();
      this.bindEvents();
      this.setupReveal();
      this.setupCardGlow();
      this.updateEmptyState();
    }

    configureFilters() {
      const categories = new Set(
        this.cards
          .map((card) => String(card.dataset.category || '').trim())
          .filter(Boolean)
      );

      this.filters.forEach((button) => {
        const filter = button.dataset.lsFilter;

        if (filter !== 'all' && !categories.has(filter)) {
          button.hidden = true;
        }
      });

      this.filterJumps.forEach((button) => {
        const filter = button.dataset.lsFilterJump;

        if (!categories.has(filter)) {
          button.disabled = true;
          button.setAttribute('aria-disabled', 'true');
        }
      });
    }

    bindEvents() {
      this.filters.forEach((button) => {
        button.addEventListener('click', () => {
          this.applyFilter(button.dataset.lsFilter || 'all');
        });
      });

      this.filterJumps.forEach((button) => {
        button.addEventListener('click', () => {
          if (button.disabled) return;

          const filter = button.dataset.lsFilterJump || 'all';
          this.applyFilter(filter);

          const analyses = this.root.querySelector('.ls-why-analyses');
          analyses?.scrollIntoView({
            behavior: this.reducedMotion ? 'auto' : 'smooth',
            block: 'start'
          });
        });
      });
    }

    applyFilter(filter) {
      this.activeFilter = filter;

      this.filters.forEach((button) => {
        const isActive = button.dataset.lsFilter === filter;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-pressed', String(isActive));
      });

      this.cards.forEach((card) => {
        const shouldShow = filter === 'all' || card.dataset.category === filter;
        card.hidden = !shouldShow;

        if (shouldShow) {
          requestAnimationFrame(() => card.classList.add('is-visible'));
        }
      });

      this.updateEmptyState();
    }

    updateEmptyState() {
      if (!this.emptyState) return;

      const visibleCards = this.cards.filter((card) => !card.hidden);
      this.emptyState.hidden = visibleCards.length > 0;
    }

    setupReveal() {
      const revealTargets = [...this.revealItems, ...this.cards];

      if (this.reducedMotion || !('IntersectionObserver' in window)) {
        revealTargets.forEach((item) => item.classList.add('is-visible'));
        return;
      }

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;

            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          });
        },
        {
          rootMargin: '0px 0px -8% 0px',
          threshold: 0.12
        }
      );

      revealTargets.forEach((item) => observer.observe(item));
    }

    setupCardGlow() {
      if (this.reducedMotion) return;

      this.cards.forEach((card) => {
        card.addEventListener('pointermove', (event) => {
          const bounds = card.getBoundingClientRect();
          const x = ((event.clientX - bounds.left) / bounds.width) * 100;
          const y = ((event.clientY - bounds.top) / bounds.height) * 100;

          card.style.setProperty('--ls-x', `${x.toFixed(2)}%`);
          card.style.setProperty('--ls-y', `${y.toFixed(2)}%`);
        });

        card.addEventListener('pointerleave', () => {
          card.style.setProperty('--ls-x', '50%');
          card.style.setProperty('--ls-y', '50%');
        });
      });
    }
  }

  function initialise(root = document) {
    root.querySelectorAll(ROOT_SELECTOR).forEach((section) => {
      if (section.dataset.lsWhyInitialised === 'true') return;

      section.dataset.lsWhyInitialised = 'true';
      new LoveSmithsWhyChoose(section).init();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialise());
  } else {
    initialise();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initialise(event.target);
  });
})();
